import { inject, Injectable } from '@angular/core';
import { Store } from '@fc-core/store-utils/store';
import { Store as NgRxStore } from '@ngrx/store';
import { storeResource } from '@fc-core/store-utils/resource';
import { getCurrentCustomer } from '@fc-store/selectors/current-customer.selector';
import { SearchApiService } from '../services/search-api.service';
import {
  QuickSearchApiItem,
  QuickSearchType,
} from '@fc-search/models/quick-search';

const RECENT_ITEMS_LIMIT = 5;

interface QuickSearchStoreState {
  query: string;
  selectedType: QuickSearchType;
  results: QuickSearchApiItem[];
  total: number;
  loading: boolean;
  error: unknown;
}

const initialState: QuickSearchStoreState = {
  query: '',
  selectedType: 'all',
  results: [],
  total: 0,
  loading: false,
  error: null,
};

@Injectable({
  providedIn: 'root',
})
export class QuickSearchStore extends Store<QuickSearchStoreState> {
  private api = inject(SearchApiService);
  private ngrxStore = inject(NgRxStore);
  private currentCustomer = this.ngrxStore.selectSignal(getCurrentCustomer);

  readonly query = this.selectors.query;
  readonly selectedType = this.selectors.selectedType;
  readonly results = this.selectors.results;
  readonly total = this.selectors.total;
  readonly loading = this.selectors.loading;

  readonly recentItems = storeResource({
    params: () => this.currentCustomer()?.id,
    loader: () => this.api.getRecentQuickSearchItems(RECENT_ITEMS_LIMIT),
  });

  constructor() {
    super(initialState);
  }

  setQuery(value: string): void {
    this.update({ query: value });
  }

  setType(type: QuickSearchType): void {
    this.update({
      selectedType: type,
      results: [],
      total: 0,
      loading: false,
      error: null,
    });
  }

  clearResults(): void {
    this.update({ results: [], total: 0, loading: false, error: null });
  }

  clearSearch(): void {
    this.update({ ...initialState });
  }

  async search(): Promise<void> {
    const query = this.query();
    const type = this.selectedType();
    this.update({ loading: true, error: null });
    try {
      const items = await this.api.getQuickSearchResults(query, type);
      this.update({
        results: items,
        total: items.length,
        loading: false,
      });
    } catch (err) {
      this.update({ error: err, loading: false });
    }
  }
}
